import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, qk, useWorkspaceFilms } from '../hooks/queries';
import {
  AlertTriangle, AlertCircle, Info, RefreshCw, Sparkles,
  ChevronRight, Clock, Film as FilmIcon, Brain,
  Target, Eye, Shield, DollarSign, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useWorkspace } from '../context/WorkspaceContext';
import { cn } from '../lib/utils';

const SEVERITY_MAP = {
  critical: { label: 'Critical', color: 'bg-destructive/15 text-destructive border-destructive/30', dot: 'bg-destructive', icon: AlertTriangle },
  warn: { label: 'Warning', color: 'bg-warning/15 text-warning border-warning/30', dot: 'bg-warning', icon: AlertCircle },
  info: { label: 'Info', color: 'bg-info/15 text-info border-info/30', dot: 'bg-info', icon: Info },
};

const CATEGORY_MAP = {
  progress: { label: 'Progress', icon: Target, color: 'text-blue-500' },
  bottleneck: { label: 'Bottleneck', icon: Clock, color: 'text-amber-500' },
  coverage: { label: 'Coverage', icon: Eye, color: 'text-purple-500' },
  risk: { label: 'Risk', icon: Shield, color: 'text-red-500' },
  cost: { label: 'Cost', icon: DollarSign, color: 'text-green-500' },
};

function InsightCard({ insight, navigate }) {
  const sev = SEVERITY_MAP[insight.severity] || SEVERITY_MAP.info;
  const cat = CATEGORY_MAP[insight.category] || CATEGORY_MAP.progress;
  const SevIcon = sev.icon;
  const CatIcon = cat.icon;

  // Accept workspaceId as a prop for navigation
  const workspaceId = insight._workspaceId;

  const handleEntityClick = () => {
    if (!insight.entity_ref) return;
    const { type, id } = insight.entity_ref;
    if (type === 'scene') navigate(`/scenes/${id}`);
    else if (type === 'film' && workspaceId) {
      navigate(`/workspaces/${workspaceId}/films/${id}`);
    }
  };
  return (
    <div className="rounded-xl border bg-card p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3">
        <span className={cn('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg', sev.color)}>
          <SevIcon className="size-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider', sev.color)}>
              {sev.label}
            </span>
            <span className={cn('inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider', cat.color)}>
              <CatIcon className="size-3" /> {cat.label}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-foreground leading-snug">{insight.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{insight.body}</p>

          {insight.narrative && (
            <div className="mt-3 rounded-lg bg-muted/50 border border-border/50 px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Brain className="size-3 text-muted-foreground" />
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">AI Analysis</span>
              </div>
              <p className="text-sm italic text-muted-foreground leading-relaxed">{insight.narrative}</p>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {insight.entity_ref && (
              <button
                onClick={handleEntityClick}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <FilmIcon className="size-3" />
                {insight.entity_ref.name || insight.entity_ref.id}
                <ChevronRight className="size-3" />
              </button>
            )}
            {insight.suggested_action && (
              <span className="text-xs text-muted-foreground">
                → {insight.suggested_action}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-xl border bg-card p-5 animate-pulse">
          <div className="flex gap-3">
            <div className="size-8 rounded-lg bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="flex gap-2">
                <div className="h-4 w-16 rounded bg-muted" />
                <div className="h-4 w-20 rounded bg-muted" />
              </div>
              <div className="h-4 w-3/4 rounded bg-muted" />
              <div className="h-3 w-full rounded bg-muted" />
              <div className="h-3 w-2/3 rounded bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AIInsights() {
  const { currentWorkspace, workspaces } = useWorkspace();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [severityFilter, setSeverityFilter] = useState('all');
  const [filmFilter, setFilmFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Use currentWorkspace from context. If none is selected but workspaces are
  // loaded, fall back to the first workspace (common when user lands directly
  // on /ai-insights without navigating through a workspace first).
  const fallbackWsQuery = useQuery({
    queryKey: ['workspaces', 'fallback'],
    queryFn: ({ signal }) => apiGet('/workspaces', signal),
    enabled: !currentWorkspace && (!workspaces || workspaces.length === 0),
    staleTime: 60_000,
  });

  const resolvedWorkspace = currentWorkspace
    || (workspaces && workspaces.length > 0 ? workspaces[0] : null)
    || (fallbackWsQuery.data && fallbackWsQuery.data.length > 0 ? fallbackWsQuery.data[0] : null);

  const workspaceId = resolvedWorkspace?.id;

  const filmsQuery = useWorkspaceFilms(workspaceId);
  const films = useMemo(() => filmsQuery.data || [], [filmsQuery.data]);

  const insightsQuery = useQuery({
    queryKey: [...qk.workspaceInsights(workspaceId), 'full'],
    queryFn: ({ signal }) => apiGet(`/ai/insights/workspace/${workspaceId}`, signal),
    enabled: !!workspaceId,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const handleRefresh = useCallback(async () => {
    if (!workspaceId) return;
    setIsRefreshing(true);
    try {
      const data = await apiGet(`/ai/insights/workspace/${workspaceId}?refresh=1`);
      queryClient.setQueryData([...qk.workspaceInsights(workspaceId), 'full'], data);
      // Also invalidate the card query
      queryClient.invalidateQueries({ queryKey: qk.workspaceInsights(workspaceId) });
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [workspaceId, queryClient]);

  const data = insightsQuery.data;
  const insights = useMemo(() => {
    if (!data?.insights) return [];
    let filtered = data.insights;
    if (severityFilter !== 'all') {
      filtered = filtered.filter((i) => i.severity === severityFilter);
    }
    if (filmFilter !== 'all') {
      filtered = filtered.filter(
        (i) => i.entity_ref?.id === filmFilter || i.entity_ref?.name === filmFilter
      );
    }
    return filtered;
  }, [data, severityFilter, filmFilter]);

  const counts = data?.counts || { critical: 0, warn: 0, info: 0 };

  if (!workspaceId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Sparkles className="size-12 text-muted-foreground mx-auto mb-3" />
          <h2 className="text-lg font-semibold">Select a workspace</h2>
          <p className="text-sm text-muted-foreground mt-1">Choose a workspace from the sidebar to view AI insights.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="size-10 rounded-xl bg-warning/15 text-warning flex items-center justify-center">
            <Brain className="size-5" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">AI Insights</h1>
            {data && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Generated {new Date(data.generated_at).toLocaleString()}
                {data.cached && <span className="ml-1 text-muted-foreground/60">(cached)</span>}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl gap-1.5"
          onClick={handleRefresh}
          disabled={isRefreshing || insightsQuery.isLoading}
        >
          <RefreshCw className={cn('size-3.5', (isRefreshing || insightsQuery.isFetching) && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Summary counters */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setSeverityFilter('all')}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors',
            severityFilter === 'all'
              ? 'bg-foreground text-background border-foreground'
              : 'bg-card text-muted-foreground border-border hover:bg-muted'
          )}
        >
          All ({(counts.critical || 0) + (counts.warn || 0) + (counts.info || 0)})
        </button>
        {Object.entries(SEVERITY_MAP).map(([key, { label, dot }]) => (
          <button
            key={key}
            onClick={() => setSeverityFilter(severityFilter === key ? 'all' : key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors',
              severityFilter === key
                ? 'bg-foreground text-background border-foreground'
                : 'bg-card text-muted-foreground border-border hover:bg-muted'
            )}
          >
            <span className={cn('size-2 rounded-full', dot)} />
            {label} ({counts[key] || 0})
          </button>
        ))}

        {films.length > 1 && (
          <select
            value={filmFilter}
            onChange={(e) => setFilmFilter(e.target.value)}
            className="rounded-full px-3 py-1.5 text-xs font-medium border border-border bg-card text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="all">All Films</option>
            {films.map((f) => (
              <option key={f.id} value={f.id}>{f.title}</option>
            ))}
          </select>
        )}

        {(severityFilter !== 'all' || filmFilter !== 'all') && (
          <button
            onClick={() => { setSeverityFilter('all'); setFilmFilter('all'); }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="size-3" /> Clear filters
          </button>
        )}
      </div>

      {/* Content */}
      {insightsQuery.isLoading ? (
        <SkeletonCards />
      ) : insightsQuery.isError ? (
        <div className="rounded-xl border bg-card p-8 text-center">
          <AlertTriangle className="size-10 text-destructive mx-auto mb-3" />
          <h3 className="text-sm font-semibold">Failed to load insights</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-4">
            {insightsQuery.error?.response?.data?.message || insightsQuery.error?.message || 'Unknown error'}
          </p>
          <Button variant="outline" size="sm" onClick={() => insightsQuery.refetch()}>Retry</Button>
        </div>
      ) : insights.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <span className="size-14 rounded-2xl bg-success/15 text-success flex items-center justify-center mx-auto mb-4">
            <Sparkles className="size-6" />
          </span>
          <h3 className="text-base font-semibold">No active insights</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {films.length === 0
              ? 'Add a film to start surfacing production insights.'
              : 'Everything looks healthy across your productions.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {insights.map((insight) => (
            <InsightCard key={insight.id} insight={{...insight, _workspaceId: workspaceId}} navigate={navigate} />
          ))}
        </div>
      )}

      {/* Skipped categories notice */}
      {data?.meta?.skipped?.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          Some insight categories were skipped due to missing data: {data.meta.skipped.join(', ')}.
        </p>
      )}
    </div>
  );
}
